import React, { useEffect, useState } from 'react'
import api from 'config/axiosConfig'
import IssueCard from 'components/Issues/IssueCard'
import AddIssueToSolution from 'components/Issues/AddIssueToSolution'
import { Issue } from 'util/constants'
import AddButton from 'ui/AddButton'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import { useUser } from 'util/UserContext'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import { AxiosError } from 'axios'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import ScrollHideContainer from 'ui/ScrollHideContainer'

interface Props {
  solutionId: number
}

interface RelatedIssue extends Issue {
  issue_id: number
}

const SolutionIssues = ({ solutionId }: Props) => {
  const { currentUser, setShowAuthModal } = useUser()
  const [relatedIssues, setRelatedIssues] = useState<RelatedIssue[] | null>(null)
  const [error, setError] = useState('')
  const [isAddIssueModalOpen, setAddIssueModalOpen] = useState(false)

  useEffect(() => {
    const fetchRelatedIssues = async () => {
      try {
        const response = await api.get<RelatedIssue[]>(
          `/api/solutions/${solutionId}/issues`
        )
        const issueIds = response.data.map((issue) => issue.issue_id)
        if (issueIds.length > 0) {
          const issuesResponse = await api.get<RelatedIssue[]>(
            `/api/issues?issueIds=${JSON.stringify(issueIds)}`
          )
          setRelatedIssues(issuesResponse.data)
        } else {
          setRelatedIssues([])
        }
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 404) {
          setError('404: Failed to load related issues. Please try again later.')
        } else {
          setError('Failed to load related issues. Please try again later.')
        }
      }
    }

    fetchRelatedIssues()
  }, [solutionId])

  const handleIssueAdded = (newIssue: RelatedIssue) => {
    setRelatedIssues((prevIssues) =>
      prevIssues ? [...prevIssues, newIssue] : [newIssue]
    )
  }

  const handleAddIssueClick = () => {
    if (!currentUser) {
      setShowAuthModal(true)
    } else {
      setAddIssueModalOpen(true)
    }
  }

  if (error) {
    return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find the issues solved by this solution.`} buttonText={`View Other Issues`} buttonHref={`/issues/new`} /> : <ServerErrorPage />
  }

  if (relatedIssues === null) {
    return (
      <LoadingSkeleton count={5} />
    )
  }

  return (
    <div className="related-issues-container pb-6">
      <AddIssueToSolution
        solutionId={solutionId}
        onIssueAdded={handleIssueAdded}
        open={isAddIssueModalOpen}
        onClose={setAddIssueModalOpen}
        existingIssues={relatedIssues || []}
      />
      <ScrollHideContainer 
        className="z-40 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg border-b border-eoe-border-main dark:border-eoe-dark-border-main"
        hideOffset={115}
        threshold={114}
      >
      <div className="flex flex-col space-y-1 mb-1">
        <div className="flex mb-1 mx-1">
        <AddButton
          tooltipTitle='Suggest this solution for an issue'
          onClick={handleAddIssueClick}
          buttonText='Issue'
          className='mx-2 w-full'
        />
        </div>
      </div>
      </ScrollHideContainer>
      {relatedIssues.length > 0 ? (
        <div className="entity-divider">
          {relatedIssues.map((issue) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              showMeta={true}
              showActions={true}
              onVote={() => {}}
              truncateDescription={true}
            />
          ))}
        </div>
      ) : (
        <p className="text-eoe-text dark:text-eoe-dark-text">
          No related issues found.
        </p>
      )}
    </div>
  )
}

export default SolutionIssues
